import { fromDayNumber, toDayNumber } from '../../recurrence/localDate.js';
// De joodse (Hebreeuwse) kalender, lokaal uitgerekend (module Jodendom, oprichter 2 okt 2026:
// "bouw direct de modules"). Geen bibliotheek en geen verzoek naar een derde: een eigen,
// kleine uitwerking van het vaste rekenschema (molad en de uitstelregels, de dehiyyot) zoals
// beschreven in E.M. Reingold en N. Dershowitz, "Calendrical Calculations" (Cambridge University
// Press, 4e druk 2018), hoofdstuk 8 "The Hebrew Calendar". Alleen het algoritme is overgenomen,
// geen code. Gecontroleerd tegen Hebcal (www.hebcal.com, data CC BY 4.0) voor 2019-2040 in
// hebrewCalendar.test.ts.
//
// Dagnummers: zoals recurrence/localDate.ts (dagen sinds 1970-01-01, via Date.UTC). "Rata Die"
// (RD, dag 1 = 1 januari jaar 1) = dagnummer + 719163.
//
// Maandnummers zoals Reingold en Dershowitz: Nisan = 1 ... Elloel = 6, Tisjri = 7, Chesjwan = 8,
// Kislev = 9, Tevet = 10, Sjevat = 11, Adar (Adar I in een schrikkeljaar) = 12, Adar II = 13. Het
// jaar begint op 1 Tisjri (Rosj Hasjana); 1 Nisan valt halverwege het jaar.
//
// LET OP (review): alle joodse teksten en regels in deze map moeten vóór de publieke lancering
// worden nagekeken door een rabbijn of een lid van de gemeenschap.
export const NISAN = 1;
export const IYAR = 2;
export const SIVAN = 3;
export const TAMMUZ = 4;
export const AV = 5;
export const ELUL = 6;
export const TISHRI = 7;
export const CHESHVAN = 8;
export const KISLEV = 9;
export const TEVET = 10;
export const SHEVAT = 11;
export const ADAR = 12;
export const ADAR_II = 13;
/** RD van 1 januari 1970 (dagnummer 0). */
const RD_1970 = 719163;
/** RD van de dag vóór 1 Tisjri jaar 1 (het "epoch" van Reingold en Dershowitz): −1373427. */
const HEBREW_EPOCH = -1373427;
const rdOf = (date) => toDayNumber(date) + RD_1970;
const dateOfRd = (rd) => fromDayNumber(rd - RD_1970);
/** Schrikkeljaar (13 maanden): jaren 3, 6, 8, 11, 14, 17 en 19 van de cyclus van 19 jaar. */
export function isHebrewLeapYear(year) {
    return (((7 * year + 1) % 19) + 19) % 19 < 7;
}
/** Laatste maand van het jaar: Adar (12) of Adar II (13). */
export function lastMonthOfYear(year) {
    return isHebrewLeapYear(year) ? ADAR_II : ADAR;
}
/**
 * Dagen vóór Rosj Hasjana van `year` sinds het epoch, met de molad en de regel "lo ADU Rosj"
 * (1 Tisjri nooit op zondag, woensdag of vrijdag). Een maand duurt 29 dagen, 12 uur en 793 delen
 * (1 uur = 1080 delen); 12084 delen is de molad van Tisjri jaar 1 na het epoch.
 */
function elapsedDays(year) {
    const monthsElapsed = Math.floor((235 * year - 234) / 19);
    const partsElapsed = 12084 + 13753 * monthsElapsed;
    const days = 29 * monthsElapsed + Math.floor(partsElapsed / 25920);
    return (3 * (days + 1)) % 7 < 3 ? days + 1 : days;
}
/**
 * De overige uitstelregels: een jaar mag niet 356 dagen duren (dan schuift het volgende
 * nieuwjaar 2 dagen op) en het vorige niet 382 (dan dit nieuwjaar 1 dag).
 */
function yearLengthCorrection(year) {
    const ny0 = elapsedDays(year - 1);
    const ny1 = elapsedDays(year);
    const ny2 = elapsedDays(year + 1);
    if (ny2 - ny1 === 356)
        return 2;
    if (ny1 - ny0 === 382)
        return 1;
    return 0;
}
const newYearCache = {};
/** RD van 1 Tisjri van `year`. */
function newYearRd(year) {
    const hit = newYearCache[year];
    if (hit !== undefined)
        return hit;
    const rd = HEBREW_EPOCH + elapsedDays(year) + yearLengthCorrection(year);
    newYearCache[year] = rd;
    return rd;
}
/** Rosj Hasjana (1 Tisjri) van joods jaar `year` als gewone datum. */
export function roshHashanaOf(year) {
    return dateOfRd(newYearRd(year));
}
/** Lengte van het joodse jaar: 353, 354 of 355 dagen (gewoon), 383, 384 of 385 (schrikkel). */
export function daysInHebrewYear(year) {
    return newYearRd(year + 1) - newYearRd(year);
}
/** "Volledig" jaar: Chesjwan heeft dan 30 dagen. */
export function longCheshvan(year) {
    return daysInHebrewYear(year) % 10 === 5;
}
/** "Gebrekkig" jaar: Kislev heeft dan 29 dagen. */
export function shortKislev(year) {
    return daysInHebrewYear(year) % 10 === 3;
}
/** Aantal dagen in een maand (29 of 30). */
export function daysInHebrewMonth(year, month) {
    if (month === IYAR ||
        month === TAMMUZ ||
        month === ELUL ||
        month === TEVET ||
        month === ADAR_II ||
        (month === ADAR && !isHebrewLeapYear(year)) ||
        (month === CHESHVAN && !longCheshvan(year)) ||
        (month === KISLEV && shortKislev(year))) {
        return 29;
    }
    return 30;
}
/** RD van een joodse datum. */
function rdOfHebrew(h) {
    let rd = newYearRd(h.year) + h.day - 1;
    if (h.month < TISHRI) {
        // Nisan t/m Elloel: eerst de hele maanden vanaf Tisjri tot het eind van het jaar.
        const last = lastMonthOfYear(h.year);
        for (let m = TISHRI; m <= last; m += 1)
            rd += daysInHebrewMonth(h.year, m);
        for (let m = NISAN; m < h.month; m += 1)
            rd += daysInHebrewMonth(h.year, m);
    }
    else {
        for (let m = TISHRI; m < h.month; m += 1)
            rd += daysInHebrewMonth(h.year, m);
    }
    return rd;
}
/** De gewone datum van een joodse datum. */
export function fromHebrew(h) {
    return dateOfRd(rdOfHebrew(h));
}
/** De joodse datum van een gewone datum (de dag zelf; de avond ervoor hoort er al bij). */
export function toHebrew(date) {
    const rd = rdOf(date);
    // Gemiddeld jaar: 35975351/98496 dagen. Begin één jaar lager en loop vooruit.
    let year = Math.floor((rd - HEBREW_EPOCH) / (35975351 / 98496));
    while (newYearRd(year + 1) <= rd)
        year += 1;
    while (newYearRd(year) > rd)
        year -= 1;
    const nisanFirst = rdOfHebrew({ year, month: NISAN, day: 1 });
    let month = rd < nisanFirst ? TISHRI : NISAN;
    for (;;) {
        const end = rdOfHebrew({ year, month, day: daysInHebrewMonth(year, month) });
        if (rd <= end)
            break;
        month += 1;
    }
    const day = rd - rdOfHebrew({ year, month, day: 1 }) + 1;
    return { year, month, day };
}
/**
 * Het joodse jaar waarin `date` valt (een gewone datum in september-oktober kan in twee
 * joodse jaren liggen: vóór en na Rosj Hasjana).
 */
export function hebrewYearOf(date) {
    return toHebrew(date).year;
}
/**
 * Sleutels van de maandnamen (de namen zelf staan per taal in packages/i18n, jodendom.*.ts). De
 * eerste twaalf volgen de maandnummers hierboven; Adar heet in een schrikkeljaar Adar I en Adar II.
 */
export const HEBREW_MONTH_KEYS = [
    'nisan',
    'iyar',
    'sivan',
    'tammuz',
    'av',
    'elul',
    'tishri',
    'cheshvan',
    'kislev',
    'tevet',
    'shevat',
    'adar',
    'adar_1',
    'adar_2',
];
/** Sleutel van de maandnaam: Adar heet in een schrikkeljaar Adar I (12) en Adar II (13). */
export function hebrewMonthKey(year, month) {
    if (month === ADAR)
        return isHebrewLeapYear(year) ? 'adar_1' : 'adar';
    if (month === ADAR_II)
        return 'adar_2';
    return HEBREW_MONTH_KEYS[month - 1];
}
/** Maandnamen in Hebreeuws schrift (optioneel op het bord). */
export const HEBREW_MONTH_SCRIPT = {
    nisan: 'ניסן',
    iyar: 'אייר',
    sivan: 'סיון',
    tammuz: 'תמוז',
    av: 'אב',
    elul: 'אלול',
    tishri: 'תשרי',
    cheshvan: 'חשון',
    kislev: 'כסלו',
    tevet: 'טבת',
    shevat: 'שבט',
    adar: 'אדר',
    adar_1: 'אדר א׳',
    adar_2: 'אדר ב׳',
};
const ONES = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];
const TENS = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
const HUNDREDS = ['', 'ק', 'ר', 'ש', 'ת'];
/**
 * Getal in Hebreeuwse letters (gematria) met geresj (׳) of gersjajim (״): 1 → א׳, 15 → ט״ו (niet
 * יה), 16 → ט״ז, 787 → תשפ״ז. Duizendtallen vallen weg (5787 → תשפ״ז, zoals op elke luach).
 */
export function hebrewNumeral(n) {
    let rest = Math.floor(n) % 1000;
    let letters = '';
    while (rest >= 400) {
        letters += 'ת';
        rest -= 400;
    }
    if (rest >= 100) {
        letters += HUNDREDS[Math.floor(rest / 100)] ?? '';
        rest %= 100;
    }
    if (rest === 15)
        letters += 'טו';
    else if (rest === 16)
        letters += 'טז';
    else {
        letters += TENS[Math.floor(rest / 10)] ?? '';
        letters += ONES[rest % 10] ?? '';
    }
    if (letters.length === 0)
        return '';
    if (letters.length === 1)
        return `${letters}׳`;
    return `${letters.slice(0, -1)}״${letters.slice(-1)}`;
}
/** "כ׳ תשרי תשפ״ז": de datum in Hebreeuws schrift. */
export function hebrewDateScript(h) {
    return `${hebrewNumeral(h.day)} ${HEBREW_MONTH_SCRIPT[hebrewMonthKey(h.year, h.month)]} ${hebrewNumeral(h.year)}`;
}
/** Dag van de week van een gewone datum: 0 = zondag ... 6 = sjabbat. */
export function dayOfWeek(date) {
    return (((toDayNumber(date) + 4) % 7) + 7) % 7;
}
//# sourceMappingURL=hebrewCalendar.js.map